import { isDefined } from "@clipboard-health/util-ts";
import { CalendarGridMonth } from "@src/appV2/redesign/Calendar/GridMonth";
import { getBookedShiftTimeSlotsFromWorkerDayShifts } from "@src/appV2/redesign/utils/getBookedShiftTimeSlotsFromWorkerDayShifts";
import type { ShiftsByDateAndTimeSlot } from "@src/appV2/redesign/utils/groupShiftsByDateAndTimeSlot";
import { formatISO, isToday, startOfDay } from "date-fns";

import { ShiftCalendarCell } from "./ShiftCalendarCell";
import type { OpenShiftDatesResponseWithUrgent } from "./types";

interface ShiftCalendarCoreProps {
  startDate: Date;
  showMonthTitle?: boolean;
  weekSpacing?: number;
  isLoading: boolean;
  filteredOpenShiftsCountData?: OpenShiftDatesResponseWithUrgent;
  workerShiftsByDate?: ShiftsByDateAndTimeSlot;
  selectedDates: string[];
  onDateToggle: (date: string, isSelected: boolean) => void;
  isMonthInLoadedRange?: (monthDate: Date) => boolean;
  showPriorityShifts?: boolean;
  showUrgentShifts?: boolean;
}

export function ShiftCalendarCore(props: ShiftCalendarCoreProps) {
  const {
    startDate,
    showMonthTitle = true,
    weekSpacing,
    isLoading,
    filteredOpenShiftsCountData,
    workerShiftsByDate,
    selectedDates,
    onDateToggle,
    isMonthInLoadedRange,
    showPriorityShifts = false,
    showUrgentShifts = false,
  } = props;

  const getDateShiftData = (formattedDate: string, isMonthLoaded: boolean) => {
    if (!isMonthLoaded) {
      return {
        isDateAvailable: false,
        hasPriorityShift: false,
        hasUrgentShift: false,
        bookedShiftTimeSlots: [],
      };
    }

    const dateOpenShifts = filteredOpenShiftsCountData?.[formattedDate];
    const isDateAvailable = isDefined(dateOpenShifts);

    const hasPriorityShift =
      showPriorityShifts && (dateOpenShifts?.priorityAccess?.length ?? 0) > 0;

    const hasUrgentShift = showUrgentShifts && (dateOpenShifts?.urgentShifts?.length ?? 0) > 0;

    const workerDayShifts = workerShiftsByDate?.[formattedDate];
    const bookedShiftTimeSlots = getBookedShiftTimeSlotsFromWorkerDayShifts(workerDayShifts);

    return {
      isDateAvailable,
      hasPriorityShift,
      hasUrgentShift,
      bookedShiftTimeSlots,
    };
  };

  const createDateToggleHandler = (
    formattedDate: string,
    isMonthLoaded: boolean,
    isPastDate: boolean
  ) => {
    return (isSelected: boolean) => {
      if (!isMonthLoaded || isPastDate) {
        return;
      }

      onDateToggle(formattedDate, isSelected);
    };
  };

  const renderDateCell = (date: Date) => {
    const isDateToday = isToday(date);
    const formattedDate = formatISO(date, { representation: "date" });
    const isMonthLoaded = isMonthInLoadedRange?.(startDate) ?? true;
    const isCurrentMonthLoading = !isMonthLoaded && isLoading;
    const isPastDate = date < startOfDay(new Date());

    const { isDateAvailable, hasPriorityShift, hasUrgentShift, bookedShiftTimeSlots } =
      getDateShiftData(formattedDate, isMonthLoaded);

    return (
      <ShiftCalendarCell
        date={date}
        isToday={isDateToday}
        isLoading={isCurrentMonthLoading || (isMonthLoaded && isLoading)}
        isDateAvailable={isPastDate ? false : isDateAvailable}
        hasPriorityShift={hasPriorityShift}
        hasUrgentShift={hasUrgentShift}
        isSelected={selectedDates.includes(formattedDate)}
        bookedShiftTimeSlots={bookedShiftTimeSlots}
        disabled={isPastDate}
        onToggleSelect={createDateToggleHandler(formattedDate, isMonthLoaded, isPastDate)}
      />
    );
  };

  const renderPastCell = (date: Date) => {
    const formattedDate = formatISO(date, { representation: "date" });
    const isMonthLoaded = isMonthInLoadedRange?.(startDate) ?? true;

    const workerDayShifts = isMonthLoaded ? workerShiftsByDate?.[formattedDate] : undefined;
    const bookedShiftTimeSlots = isMonthLoaded
      ? getBookedShiftTimeSlotsFromWorkerDayShifts(workerDayShifts)
      : [];

    return (
      <ShiftCalendarCell
        disabled
        date={date}
        isDateAvailable={false}
        bookedShiftTimeSlots={bookedShiftTimeSlots}
      />
    );
  };

  return (
    <CalendarGridMonth
      startDate={startDate}
      showMonthTitle={showMonthTitle}
      weekSpacing={weekSpacing}
      renderDateCell={renderDateCell}
      renderPastCell={renderPastCell}
    />
  );
}
